'use client'

import { AlertTriangle } from 'lucide-react'
import { dictionary } from '@/utils/dictionary'
import { useAuth } from '@/context/AuthContext'

interface ConfirmCreateAbTestModalProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  loading?: boolean
}

export function ConfirmCreateAbTestModal({
  open,
  onClose,
  onConfirm,
  loading = false
}: ConfirmCreateAbTestModalProps) {
  const { language } = useAuth()
  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">
            {dictionary['create-ab-test'][language]}
          </h3>
        </div>

        <div className="space-y-3 text-sm text-gray-600">
          <p>
            {dictionary['you-are-about-to-create-an'][language]}
            <strong> {dictionary['ab-testing'][language]} </strong>
            {dictionary['for-this-funnel'][language]}:.
          </p>

          <ul className="list-disc pl-5 space-y-1">
            <li>
              {dictionary['all-discounted-prices-will-be-disabled'][language]}
            </li>
            <li>
              {dictionary['if-there-are-other'][language]}
              <strong> {dictionary['active-ab-tests'][language]} </strong>
              {dictionary['for-this-funnel'][language]},
              <strong> {dictionary['will-be-automatically-paused'][language]} </strong>.
            </li>
          </ul>

          <p className="text-gray-500 text-xs">
            ⚠️ {dictionary['only-one-active-ab-test-per-funnel'][language]}.
          </p>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {dictionary['cancel'][language]}
          </button>

          <button
            onClick={onConfirm}
            disabled={loading}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
          >
            {loading ? dictionary['creating'][language] : dictionary['yes-create-ab-test'][language]}
          </button>
        </div>
      </div>
    </div>
  )
}
